<template>
  <div class="holder-analysis-container">
    <h3>多股票股东数分析</h3>
    <div class="input-section">
      <div class="input-row">
        <input 
          v-model="stockCodes" 
          placeholder="请输入多个股票代码（用逗号分隔），例如：000001,000002,000004" 
          class="stocks-input"
          @keyup.enter="analyze"
          @focus="selectAllOnFocus"
        />
        <button @click="analyze" class="analyze-btn">分析</button>
        <div class="year-limit-selector">
          <label class="radio-label">
            <input type="radio" v-model="yearLimit" :value="5" class="radio-input" /> 
            <span class="radio-text">5年</span>
          </label>
          <label class="radio-label">
            <input type="radio" v-model="yearLimit" :value="3" class="radio-input" /> 
            <span class="radio-text">3年</span>
          </label>
        </div>
      </div>
    </div>
    <div v-if="results.length > 0 && !isTableData" class="result-section">
      <h4>分析结果</h4>
      <div v-for="(result, index) in results" :key="index" class="result-item">
        <h5>{{ result.code }} 分析结果</h5>
        <pre>{{ result.data }}</pre>
      </div>
    </div>
    
    <!-- 以表格形式显示数据 -->
    <div v-if="tableData && tableData.length > 0" class="result-section">
      <h4>分析结果</h4>
      <div class="table-container">
        <table class="data-table">
          <thead>
            <tr>
              <th>序号</th>
              <th>股票名称</th>
              <th>股票代码</th>
              <th>行业板块</th>
              <th>3年分数</th>
              <th>5年分数</th>
              <th>户均（万）</th>
              <th>现股东数</th>
              <th>现股东日期</th>
              <th>最少股东数</th>
              <th>最少股东日期</th>
              <th>最多股东数</th>
              <th>最多股东日期</th>
              <th>上市日期</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in getSortedTableData()" :key="item.id">
              <td>{{ index + 1 }}</td>
              <td>
                <span class="width-70">
                  <a :href="$router.resolve({ name: 'SingleStockHolder', params: { stockCode: item.stockCode, yearLimit: internalYearLimit } }).href" target="_blank" class="stock-link">{{ item.stockName }}</a>
                </span>
                <span class="seperator">|</span>
                <span><a :href="$router.resolve({ name: 'HealthCheck', params: { stockCode: item.stockCode } }).href" target="_blank" class="stock-link">检</a></span>
              </td>
              <td>{{ item.stockCode }}</td>
              <td><router-link :to="{ name: 'IndustryPlateHolders', params: { plateId: item.plate_id } }" class="stock-link">{{ item.plateName }}</router-link></td>
              <td :class="getRankingScoreClass(getScoreValue(item, 3))" class="text-right">{{ getScoreValue(item, 3).toFixed(2) }}</td>
              <td :class="['divider', getRankingScoreClass(getScoreValue(item, 5))]" class="text-right">{{ getScoreValue(item, 5).toFixed(2) }}</td>
              <!-- <td class="text-right">{{ formatNumber(getNewestNum(item)) }}</td> -->
              <td class="text-right">{{ getNewestNumCap(item).toFixed(2) }}</td>
              <td class="text-right">
                <span>{{ formatNumber(getNewestNum(item)) }}</span>
                <span class="seperator-left">|</span>
                <span class="width-40">{{ getNewestNumChange(item).toFixed(0) }}%</span>
              </td>
              <td>{{ getNewestDate(item) }}</td>
              <td class="text-right">{{ formatNumber(getLowestNum(item)) }}</td>
              <td>{{ getLowestDate(item) }}</td>
              <td class="text-right">{{ formatNumber(getHighestNum(item)) }}</td>
              <td>{{ getHighestDate(item) }}</td>
              <td>{{ item.listingDate }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    
    <!-- 加载状态 -->
    <div v-if="isLoading" class="result-section">
      <h4>分析结果</h4>
      <div class="loading">正在获取数据...</div>
    </div>
    
    <!-- 错误状态 -->
    <div v-if="hasError" class="result-section">
      <h4>分析结果</h4>
      <div class="error">{{ errorMessage }}</div>
    </div>
  </div>
</template>

<script>
import mockApi from '@/api/mock';
import { formatNumber, getRankingScoreClass } from '@/utils/utils';
import '@/assets/common-styles.css';

export default {
  name: 'MultipleStockScore',
  data() {
    // 设置yearLimit为默认值5，不再从localStorage读取
    return {
      stockCodes: '',
      yearLimit: 5, // 默认选中5年
      results: [],
      tableData: [], // 用于存储表格数据
      isLoading: false, // 加载状态
      hasError: false, // 错误状态
      errorMessage: '' // 错误信息
    };
  },
  async mounted() {
    // 如果有传入的stockCodes参数，则自动分析
    if (this.stockCodes) {
      await this.analyze();
    }
  },
  computed: {
    // 判断是否为表格数据
    isTableData() {
      return this.tableData && this.tableData.length > 0;
    }
  },
  methods: {
    getRankingScoreClass,
    async analyze() {
      let codes = [];
      
      // 判断输入字符串是否全是数字
      if (/^\d+$/.test(this.stockCodes.replace(/\s/g, ''))) {
        // 如果全是数字，按每6个数字分割
        const cleanedInput = this.stockCodes.replace(/\s/g, '');
        for (let i = 0; i < cleanedInput.length; i += 6) {
          const code = cleanedInput.substring(i, i + 6);
          if (code.length === 6) { // 确保是完整的6位数字
            codes.push(code);
          }
        }
      } else {
        // 支持多种分隔符：英文逗号、中文逗号、空格、中文顿号、分号、竖线
        const separators = /[,，\s、;；|]+/;
        codes = this.stockCodes.split(separators).map(code => code.trim()).filter(code => code);
      }
      
      if (codes.length === 0) {
        alert('请输入至少一个股票代码');
        return;
      }
      
      // 重置状态
      this.isLoading = true;
      this.hasError = false;
      this.errorMessage = '';
      this.results = [];
      this.tableData = [];
      
      try {
        // 调用API获取多股票评分数据
        const response = await mockApi.getMultipleStocksScore(codes.join(','));
        
        // 隐藏加载状态
        this.isLoading = false;
        
        if (response.success) {
          // 将数据存储到tableData中用于表格展示
          this.tableData = response.data;
        } else {
          // API调用失败
          this.hasError = true;
          this.errorMessage = `获取数据失败: ${response.error || '未知错误'}`;
        }
      } catch (error) {
        // 网络或其他错误
        console.error('获取多股票评分数据失败:', error);
        this.isLoading = false;
        this.hasError = true;
        this.errorMessage = `获取数据失败: ${error.message || '网络错误'}`;
      }
    },
    // 获取分数值的辅助方法
    getScoreValue(item, year) {
      return year === 3 ? parseFloat(item.score_3y.score) : parseFloat(item.score_5y.score);
      // const scoreData = this.yearLimit === 3 ? item.score_3y : item.score_5y;
      // return parseFloat(scoreData.score);
    },
    // 获取最新股东数的辅助方法
    getNewestNum(item) {
      const scoreData = this.yearLimit === 3 ? item.score_3y : item.score_5y;
      return parseFloat(scoreData.newestNum);
    },
    // 获取最新股东数的辅助方法
    getNewestNumCap(item) {
      const scoreData = this.yearLimit === 3 ? item.score_3y : item.score_5y;
      return parseFloat(scoreData.newestCap / 10000);
    },
    getNewestNumChange(item) {
      const scoreData = this.yearLimit === 3 ? item.score_3y : item.score_5y;
      return parseFloat(scoreData.newestNumChange);
    },
    // 获取最新股东日期的辅助方法
    getNewestDate(item) {
      const scoreData = this.yearLimit === 3 ? item.score_3y : item.score_5y; 
      return scoreData.newestDate;
    },
    // 获取最少股东数的辅助方法
    getLowestNum(item) {
      const scoreData = this.yearLimit === 3 ? item.score_3y : item.score_5y;
      return scoreData.lowestNum;
    },
    // 获取最少股东日期的辅助方法
    getLowestDate(item) {
      const scoreData = this.yearLimit === 3 ? item.score_3y : item.score_5y;
      return scoreData.lowestDate;
    },
    // 获取最多股东数的辅助方法
    getHighestNum(item) {
      const scoreData = this.yearLimit === 3 ? item.score_3y : item.score_5y;
      return scoreData.highestNum;
    },
    // 获取最多股东日期的辅助方法
    getHighestDate(item) {
      const scoreData = this.yearLimit === 3 ? item.score_3y : item.score_5y;
      return scoreData.highestDate;
    },
    // 输入框获得焦点时选中所有内容
    selectAllOnFocus(event) {
      this.$nextTick(() => {
        event.target.select();
      });
    },
    
    // 根据yearLimit对tableData进行排序
    getSortedTableData() {
      if (!this.tableData || this.tableData.length === 0) {
        return [];
      }
      
      // 创建tableData的副本以避免直接修改原数组
      const sortedData = [...this.tableData];
      
      if (this.yearLimit === 3) {
        // 按照每个item的score_3y.score进行从低到高排序
        sortedData.sort((a, b) => {
          const scoreA = parseFloat(a.score_3y.score);
          const scoreB = parseFloat(b.score_3y.score);
          return scoreA - scoreB;
        });
      } else if (this.yearLimit === 5) {
        // 按照每个item的score_5y.score进行从低到高排序
        sortedData.sort((a, b) => {
          const scoreA = parseFloat(a.score_5y.score);
          const scoreB = parseFloat(b.score_5y.score);
          return scoreA - scoreB;
        });
      }
      
      return sortedData;
    },
    
    formatNumber
  }
};
</script>

<style scoped>
.holder-analysis-container {
  padding: 20px;
  max-width: 100vw;
  overflow-x: hidden;
  box-sizing: border-box;
}

.input-section {
  margin-bottom: 20px;
  max-width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
}

.input-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
}

.stocks-input {
  flex: 1;
  min-width: 200px;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1em;
  font-family: inherit;
  box-sizing: border-box;
}

.analyze-btn {
  padding: 10px 20px;
  background-color: #42b983;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.3s ease;
  white-space: nowrap;
}

.analyze-btn:hover {
  background-color: #359268;
}

.year-limit-selector {
  display: flex;
  align-items: center;
  gap: 15px;
}

.radio-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 1em;
  color: #2c3e50;
}

.radio-input {
  margin-right: 5px;
  transform: scale(1.2);
}

.radio-text {
  white-space: nowrap;
}

.result-section {
  margin-top: 20px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.result-section h4 {
  margin-top: 0;
  color: #2c3e50;
}

.result-item {
  margin-bottom: 15px;
  padding: 10px;
  border: 1px solid #eee;
  border-radius: 4px;
}

.result-item h5 {
  margin-top: 0;
  color: #2c3e50;
}

.result-item pre {
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  font-family: inherit;
  background-color: #f5f5f5;
  padding: 10px;
  border-radius: 4px;
}

/* 表格样式 */
.table-container {
  overflow-x: auto;
  max-width: 100%;
}

.data-table {
  width: 100%;
  max-width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
}

.data-table th,
.data-table td {
  padding: 12px 15px;
  text-align: left;
  border-bottom: 1px solid #ddd;
}

/* 覆盖默认左对齐，支持右对齐 */
.data-table td.text-right {
  text-align: right;
}

.data-table th {
  background-color: #f8f9fa;
  font-weight: bold;
  color: #2c3e50;
}

.data-table tbody tr:hover {
  background-color: #f5f5f5;
}

.data-table td {
  font-size: 0.9em;
}

.data-table td.divider {
  border-left: 1px solid #ffffff;
}



/* 加载和错误状态样式 */
.loading {
  padding: 20px;
  text-align: center;
  color: #42b983;
  font-weight: 500;
}

.error {
  padding: 20px;
  text-align: center;
  color: #ff4d4f;
  font-weight: 500;
  background-color: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 4px;
}

@media (max-width: 768px) {
  .input-row {
    flex-direction: column;
    align-items: stretch;
  }
  
  .stocks-input {
    min-width: unset;
  }
  
  .year-limit-selector {
    justify-content: center;
    padding: 10px 0;
  }
  
  .data-table {
    font-size: 0.8em;
  }
  
  .data-table th,
  .data-table td {
    padding: 8px 10px;
  }
}
</style>